import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useParams } from "react-router-dom";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import {
  BreadcrumbItem,
  PageTopbarItem,
} from "../../../commons/layout/Page.js";
import { ManagePropertiesOverridesTable } from "../../../features/item-properties/ManagePropertiesOverridesTable.js";
import { fromVersionNumberParamToVersionNumberInt } from "../../../utils.js";
import { useTemplateQuoteVersionPropertiesQuery } from "./TemplateQuoteVersionProperties.generated.js";

interface Props {
  docId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  pageSubHeader: React.ReactElement | null;
  pathToDocList: string;
}

export function TemplateQuoteVersionProperties({
  docId,
  submenuItems,
  prefixBreadcrumbs,
  pageSubHeader,
  pathToDocList,
}: Props) {
  const { versionNumber: versionNumberFromParam } = useParams();
  const versionNumber = fromVersionNumberParamToVersionNumberInt(
    versionNumberFromParam
  );
  if (!versionNumber) throw new Error("Version number missing");

  const { t } = useTranslate(["Global", "Templates"]);
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const query = useTemplateQuoteVersionPropertiesQuery({
    client,
    variables: { id: docId, versionNumber, applyOverrides: false },
    notifyOnNetworkStatusChange: true,
  });
  const quoteTemplate = getDataOrNull(
    (query.data ?? query.previousData)?.quoteTemplateVersion
  )?.quoteTemplate;

  return quoteTemplate ? (
    <ManagePropertiesOverridesTable
      docType="TEMPLATE"
      documentViewerPermissions={viewer.organisationPermissions}
      items={quoteTemplate.items}
      docId={docId}
      versionNumber={versionNumber}
      pageProps={{
        title: quoteTemplate?.title ?? "",
        subtitle: t("Document Templates", { ns: "Templates" }),
        submenuItems: submenuItems,
        breadcrumbs: prefixBreadcrumbs,
        subHeader: pageSubHeader,
        headerType: "template",
      }}
      refetchQueries={[namedOperations.Query.TemplateQuoteVersionProperties]}
    />
  ) : null;
}
